/*
Copyright 2022 Alexandra Instituttet

Der er givet en begrænset licens af denne software til GEO. Licensen
giver dem ret til at benytte, videreudvikle og fejlrette udelukkende
med henblik på brug i GeoAtlasStudy web-applikationen.
*/
/* eslint-disable eqeqeq */

// webasm //////////////////////////////////////////////////////////
import VoxelBrickMesher from '../webasm/VoxelBrickMesher.js';

// Imports /////////////////////////////////////////////////////////
import {WQueue, wVertexType} from 'wfundament';
import {HeightMap}           from '../HeightMap.js';
import {vec2, vec3, vec4}    from 'gl-matrix';

import {
  wgRenderingPrimitives,
  WgShader,
  WgVertexBuffer,
  WgVertexDescriptor,
} from 'wgtek';

////////////////////////////////////////////////////////////////////
const voxelVertexShaderSource = `#version 300 es
precision highp float;
in vec4 Pos;
in vec4 Normal;

flat out int voxelType;
out vec4 normalAo;
out vec2 uv;

out float zViewDepth;

uniform mat4 VP;
uniform mat4 V;
uniform mat4 modelWorld;

uniform float anim;
uniform float heightOffset;
uniform float heightScale;
uniform float domainSize;

void main() {
    vec4 position = modelWorld * vec4(Pos.xyz, 1.0) + smoothstep(1.0,0.0, anim) * vec4(0.0,0.0,1.0*heightOffset,0.0);
    uv = position.xy/domainSize;
    
    position.z = position.z*heightScale;
    voxelType = int(Pos.w);

    normalAo = vec4(Normal.x, -Normal.z, Normal.y, Normal.w);

    vec4 viewPos = V * position;
    zViewDepth = min(255.0,abs(viewPos.z) * 0.2);

    gl_Position = VP * position;
}
`;

const voxelFragmentShaderSource = `#version 300 es
precision highp float;
out vec4 outColor;

flat in int voxelType;

in vec2 uv;

in vec4 normalAo;
in float zViewDepth;


uniform sampler2D overlayTex;
uniform vec3 cameraDir;
uniform float brickAlpha;
uniform float dimLight;
uniform int useTexture;
uniform float pickingMode;


uniform sampler2D VoxelTransferTexture;

void main()
{
    vec3 norm = normalize(normalAo.xyz);
    vec3 lightDir = normalize(vec3(0.3,1.0,0.4));
    vec3 lightDir2 = cameraDir;

    float diffuseLight = max(0.2, dot(norm, lightDir) ) * 0.35;
    float directLight = max(0.3, dot(norm, lightDir2) );
    diffuseLight += directLight;
    diffuseLight = clamp(0.0,1.0, diffuseLight);



    vec4 col =  texture(VoxelTransferTexture, vec2(float(voxelType) / 255.0, 0.5 ));// getVoxelColor(voxelType);

    //vec4 col = getVoxelColor(voxelType);
    if ( useTexture != 0 && normalAo.z > 0.7)
    {
    col.rgb *= texture(overlayTex, vec2(uv.x, 1.0-uv.y)).rgb;
    }

    //float ao = smoothstep(0.0,0.8 - directLight * 0.1,normalAo.w);
    //outColor =  vec4(col.xyz * diffuseLight * ao * dimLight,brickAlpha);// normalAo * normalAo.w;


    float ao = smoothstep(0.0,0.8 - directLight * 0.1,normalAo.w);
    float fVoxelType = float(voxelType) / 255.0;
    vec4 pickingColor = vec4(fVoxelType,fVoxelType,zViewDepth / 255.0,1.0);
    outColor = (pickingMode > 0.0) ? pickingColor : vec4(col.xyz * diffuseLight * ao * dimLight,brickAlpha);

}
`;

// Globals /////////////////////////////////////////////////////////
const gVertexBufferSize = 4096 * 3 * 8; // TODO
const gHeightMapDimension = 256;

const VoxelAttributes = {
  AIR: 0,
  GRASS: 1,
  MUD: 2,
  CLAY: 3,
  SAND: 4,
  ROCK: 5,
  WATER: 6,
  MOREANELER: 7,
  FYLD: 8,
  NEDRE_LER: 9,
  KALK: 10,
  SKRIVEKRIDT: 11,
  MELLEMSAND: 12,
  OEVRESAND: 13,
  BEDROCK: 14,
  LAVA: 15,
};

const BrickClipState = {
  NOT_CLIPPED: 0,
  FULLY_CLIPPED: 1,
  CLIPPED: 2, // there is a clipping plane interscetion
};

function VoxelBrickIndexCompare(a, b) {
  if (a.depth_ <= b.depth_) {
    return 1;
  }
  return -1;
}

class VoxelBrickIndex {
  constructor(index) {
    this.index_ = index;
    this.depth_ = 0.0;
  }
}

////////////////////////////////////////////////////////////////////
class VoxelBrick {
  constructor(index, x, y) {
    this.x_ = x;
    this.y_ = y;
    this.index_ = index; // linear index into
    this.isCulled_ = false;
    this.isValid_ = false;
    this.brickClippingState_ = BrickClipState.NOT_CLIPPED;
    this.numberOfClipEdges_ = 0;

    this.cornerPosition_ = vec3.create();
    this.animation_ = 1.0;
    this.vertexBuffer_ = null;
    this.vertexCount_ = 0;
    this.alpha_ = 1.0;
    this.center_ = vec3.create();

    // bounding box
    // animation
    // info
    // perhaps voxel information
  }

  updateAnimation() {
    this.animation_ += this.isValid_ ? -0.05 : 0.05;
    this.animation_ = Math.min(1.0, Math.max(0.0, this.animation_));
  }

  getIndex() {
    return this.index_;
  }

  getVertexBuffer() {
    return this.vertexBuffer_;
  }

  getClippingState() {
    return this.brickClippingState_;
  }

  updateClippingState(clippingState) {
    if (
      clippingState === this.brickClippingState_ &&
      clippingState !== BrickClipState.CLIPPED
    ) {
      return false;
    }
    this.brickClippingState_ = clippingState; // clipping state changed, need
    // to flag for brick update.
    this.isValid_ = false;
    return true;
  }

  setClippingState(clippingState) {
    this.brickClippingState_ = clippingState;
    this.isValid_ = false;
  }
}

class VoxelClipPlanes {
  constructor() {
    this.clipXMin_ = 0;
    this.clipXMax_ = 512.0;
    this.clipZMin_ = 0;
    this.clipZMax_ = 512.0;
  }
}

////////////////////////////////////////////////////////////////////
class VoxelModel {
  constructor(
    glContext,
    resourceCache,
    brickDimension,
    brickHeight,
    voxelSizeInMeter,
    voxelHeightInMeter
  ) {
    this.context_ = glContext;
    this.resourceCache_ = resourceCache;
    this.brickDim_ = 8;
    this.brickDimension_ = brickDimension;
    this.brickHeight_ = brickHeight;
    this.heightOffset_ = 0.0;
    this.heightScale_ = 1.0;
    this.voxelSizeInMeter_ = voxelSizeInMeter;
    this.voxelHeightInMeter_ = voxelHeightInMeter;
    this.voxelCopyBrick_ = new Int32Array(
      this.brickDimension_ * this.brickDimension_ * this.brickHeight_
    );
    this.vertexBufferData_ = new Float32Array(gVertexBufferSize);

    this.voxelBricks_ = [];
    this.voxelBrickIndices_ = [];
    this.voxelData_ = null;
    this.voxelDimension_ = [0, 0, 0];
    this.voxelUpdateQueue_ = new WQueue(true);
    this.totalVertexCount_ = 0;
    this.clipPlanes_ = new VoxelClipPlanes();

    this.brickClipPolygonEdges_ = new Float32Array(128); // max 64 edges
    this.brickClipPolygonNormal_ = new Float32Array(128); // max 64 edges
    this.numberOfClipPolyEdges_ = 0;

    this.overlayTexture_ = null;
  }

  async init() {
    this.createVoxelBricks();
    this.createShaders();
    //this.createTestHeightMaps();

    this.VoxelBrickMesher_ = await VoxelBrickMesher();
    this.voxelBrickTriangulator_ = new this.VoxelBrickMesher_.VoxelBrick(
      this.brickDimension_,
      this.brickHeight_
    );
    this.voxelBrickLod1_ = new this.VoxelBrickMesher_.VoxelBrick(
      this.brickDimension_ / 2,
      this.brickHeight_ / 2
    );
  }

  setVoxelData(nx, ny, nz, data /* Array*/) {
    this.voxelDimension_[0] = nx;
    this.voxelDimension_[1] = ny;
    this.voxelDimension_[2] = nz;
    this.voxelData_ = new Int32Array(data);
  }

  setTransferTexture(voxelTransferTexture) {
    this.voxelTransferTexture_ = voxelTransferTexture;
  }

  getVoxelDimension() {
    return this.voxelDimension_;
  }

  getVoxelEmpty(x, y, z) {
    const border = 5;
    if (x < border || x >= this.voxelDimension_[0] - border) {
      return true;
    }
    if (y < border || y >= this.voxelDimension_[1] - border) {
      return true;
    }
    if (z < border || z >= this.voxelDimension_[2] - border) {
      return true;
    }

    if (x < this.clipPlanes_.clipXMin_ + border) {
      return true;
    }
    if (z < this.clipPlanes_.clipZMin_ + border) {
      return true;
    }

    return (
      this.voxelData_[
        x +
          (this.voxelDimension_[2] - 1 - z) * this.voxelDimension_[0] +
          y * this.voxelDimension_[0] * this.voxelDimension_[2]
      ] == 0
    );
  }

  setOverlayTexture(tex) {
    this.overlayTexture_ = tex;
  }

  createVoxelBricks() {
    const gl = this.context_.gl();
    let vertexT = wVertexType.VT_VertexColor4;
    let voxelVertexDescriptor = new WgVertexDescriptor(vertexT, gl);

    for (let y = 0; y < this.brickDim_; y++) {
      for (let x = 0; x < this.brickDim_; x++) {
        const brickIndex = x + y * this.brickDim_;
        const brickSortIndex = new VoxelBrickIndex(brickIndex);
        const voxelBrick = new VoxelBrick(brickIndex, x, y);

        //voxelBrick.cornerPosition_ = vec3.fromValues(x *
        // this.brickDimension_, 0.0, z * this.brickDimension_);
        vec3.set(
          voxelBrick.cornerPosition_,
          x * this.brickDimension_,
          y * this.brickDimension_,
          0.0
        );

        vec3.set(
          voxelBrick.center_,
          x * this.brickDimension_ * this.voxelSizeInMeter_ +
            this.brickDimension_ * this.voxelSizeInMeter_ * 0.5,
          (this.brickDim_ - 1 - y) *
            this.brickDimension_ *
            this.voxelSizeInMeter_ +
            this.brickDimension_ * this.voxelSizeInMeter_ * 0.5,
          0.0
        );

        /*
         voxelBrick.center_ = vec3.fromValues(
         x * this.brickDimension_ * this.voxelSizeInMeter_ + (this.brickDimension_ * this.voxelSizeInMeter_ * 0.5),
         0.0,//(this.brickHeight_ / 2.0) * this.voxelSizeInMeter_ * 0.1,
         z * this.brickDimension_ * this.voxelSizeInMeter_ + (this.brickDimension_ * this.voxelSizeInMeter_ * 0.5));
         */

        voxelBrick.vertexBuffer_ = new WgVertexBuffer(
          "VoxelVertexBuffer" + voxelBrick.getIndex(),
          this.context_
        );
        voxelBrick.vertexBuffer_.createVertexBuffer(
          gl.DYNAMIC_DRAW,
          voxelVertexDescriptor.vertexLayout_,
          gVertexBufferSize,
          voxelVertexDescriptor.getVertexSizeInBytes(),
          null
        );
        this.voxelBricks_.push(voxelBrick);
        this.voxelBrickIndices_.push(brickSortIndex);
      }
    }
  }

  findVoxelBrickIndex(index) {
    for (let i = 0; i < this.voxelBrickIndices_.length; i++) {
      if (this.voxelBrickIndices_[i].index_ == index) {
        return i;
      }
    }
    return -1;
  }

  updateVoxelBrickDepths(cameraViewMatrix) {
    for (let i = 0; i < this.voxelBricks_.length; i++) {
      let voxelBrick = this.voxelBricks_[i];
      let voxelBrickIndex = this.findVoxelBrickIndex(voxelBrick.index_);
      let c = voxelBrick.center_;
      let v = vec4.fromValues(c[0], c[1], c[2], 1.0);
      let camPos = vec4.create();
      vec4.transformMat4(camPos, v, cameraViewMatrix);
      this.voxelBrickIndices_[voxelBrickIndex].depth_ =
        -camPos[2] / this.voxelSizeInMeter_ + 0.7071 * this.brickDimension_; // z
      // is
      // negative.
      // Add
      // an
      // offset
      // to
      // prevent
      // negative
      // depth
      // if the center is behind the camera but part of the brick is still
      // visible.
    }
    this.voxelBrickIndices_.sort(VoxelBrickIndexCompare); // front to back
    // sorting for the
    // blending
  }

  createShaders() {
    this.shader_ = new WgShader("voxelTriangleShader", this.context_);
    this.shader_.createShaderProgram(
      voxelVertexShaderSource,
      voxelFragmentShaderSource,
      { Pos: 0, Normal: 1 }
    );
  }

  getVoxelColor(voxelAttrib) {
    switch (voxelAttrib) {
      case VoxelAttributes.GRASS:
        return vec4.fromValues(0.0, 1.0, 0.0, 1.0);

      case VoxelAttributes.MOREANELER:
        return vec4.fromValues(0.3, 0.3, 0.5, 1.0);

      case VoxelAttributes.CLAY:
        return vec4.fromValues(0.4, 0.4, 0.4, 1.0);

      case VoxelAttributes.SAND:
        return vec4.fromValues(0.7, 0.65, 0.15, 1.0);

      case VoxelAttributes.WATER:
        return vec4.fromValues(0.2, 0.2, 0.85, 1.0);

      case VoxelAttributes.MUD:
        return vec4.fromValues(0.5, 0.35, 0.1, 1.0);

      case VoxelAttributes.ROCK:
        return vec4.fromValues(0.25, 0.25, 0.29, 1.0);
      default:
        return vec4.fromValues(1.0, 0.0, 0.0, 1.0);
    }
  }

  getHeightOffset() {
    return this.heightOffset_;
  }

  setHeightOffset(offset) {
    this.heightOffset_ = offset;
  }

  getHeightScale() {
    return this.heightScale_;
  }

  setHeightScale(scale) {
    this.heightScale_ = scale;
  }

  update() {
    let isUpdated = false;

    if (!this.voxelUpdateQueue_.isEmpty()) {
      let peekIndex = this.voxelUpdateQueue_.peek();
      if (peekIndex != undefined) {
        if (this.voxelBricks_[peekIndex].animation_ > 0.99) {
          let index = this.voxelUpdateQueue_.pop();
          if (index != undefined) {
            this.updateBrick(index);
            isUpdated = true;
          }
        }
      }
    }
    return isUpdated;
  }

  resetAllVoxelBricks() {
    this.totalVertexCount_ = 0;
    for (let i = 0; i < this.voxelBricks_.length; i++) {
      this.voxelBricks_[i].setClippingState(BrickClipState.NOT_CLIPPED);
      this.voxelBricks_[i].isValid_ = false;
      this.voxelBricks_[i].numberOfClipEdges_ = 0;
      this.voxelUpdateQueue_.pushBack(i);
    }
  }

  updateCulling() {
    // TODO frustum culling
  }

  // TODO calculate involved bricks
  updateClipPlane() {
    this.totalVertexCount_ = 0;

    for (let i = 0; i < this.voxelBricks_.length; i++) {
      let vb = this.voxelBricks_[i];
      let vbMinx = vb.x_ * this.brickDimension_;
      let vbMaxx = vb.x_ * this.brickDimension_ + this.brickDimension_;

      let vbMinz = vb.y_ * this.brickDimension_;
      let vbMaxz = vb.y_ * this.brickDimension_ + this.brickDimension_;

      let clipState = BrickClipState.NOT_CLIPPED;

      if (
        this.clipPlanes_.clipXMin_ < vbMinx &&
        this.clipPlanes_.clipZMin_ < vbMinz
      ) {
        clipState = BrickClipState.NOT_CLIPPED;
      } else if (
        this.clipPlanes_.clipXMin_ > vbMaxx &&
        this.clipPlanes_.clipZMin_ > vbMaxz
      ) {
        clipState = BrickClipState.FULLY_CLIPPED;
      } else {
        clipState = BrickClipState.CLIPPED;
      }

      if (vb.updateClippingState(clipState)) {
        // return true if the bricks clip state
        // has been updated
        this.voxelUpdateQueue_.pushBack(i);
      }
    }
  }

  updateClipPolygon(clipPolygon) {
    this.numberOfClipPolyEdges_ = Math.min(64, clipPolygon.getNumberOfPoints());

    for (let i = 0; i < this.numberOfClipPolyEdges_ - 1; i++) {
      let p0 = clipPolygon.getPoint(i);
      let n0 = clipPolygon.getNormal(i);
      this.brickClipPolygonEdges_[i * 2] = p0[0];
      this.brickClipPolygonEdges_[i * 2 + 1] = p0[1];
      this.brickClipPolygonNormal_[i * 2] = n0[0];
      this.brickClipPolygonNormal_[i * 2 + 1] = n0[1];
    }

    for (let i = 0; i < this.voxelBricks_.length; i++) {
      const vb = this.voxelBricks_[i];
      let vbMinx = vb.x_ * this.brickDimension_;
      let vbMaxx = vb.x_ * this.brickDimension_ + this.brickDimension_;
      let vbMinz = vb.y_ * this.brickDimension_;
      let vbMaxz = vb.y_ * this.brickDimension_ + this.brickDimension_;
      //    let brickCenter = vec2.fromValues(vbMinx + (vbMaxx - vbMinx) * 0.5,
      // vbMinz + (vbMaxz - vbMinz) * 0.5);

      let brickCorners = [
        vec2.fromValues(vbMinx, vbMinz),
        vec2.fromValues(vbMaxx, vbMinz),
        vec2.fromValues(vbMaxx, vbMaxz),
        vec2.fromValues(vbMinx, vbMaxz),
      ];

      let clipState = BrickClipState.NOT_CLIPPED;
      let allPointsInside = true;
      vb.numberOfClipEdges_ = 0;

      for (let j = 0; j < brickCorners.length; j++) {
        if (!clipPolygon.isPointInside(brickCorners[j])) {
          allPointsInside = false;
          break;
        }
      }

      if (!allPointsInside) {
        if (clipPolygon.boxPolygonIntersection(brickCorners)) {
          clipState = BrickClipState.CLIPPED;
          vb.numberOfClipEdges_ = 1;
        } else {
          clipState = BrickClipState.FULLY_CLIPPED;
          //  vb.numberOfClipEdges_ = 1;
        }
      }

      if (vb.updateClippingState(clipState)) {
        // return true if the bricks clip state
        // has been updated
        this.voxelUpdateQueue_.pushBack(i);
      }
    }
  }

  render(
    cameraViewProjection,
    cameraView,
    cameraDir,
    lightValue,
    renderPickingMode
  ) {
    const gl = this.context_.gl();
    gl.disable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);

    this.voxelTransferTexture_.bind();

    this.shader_.bindProgram();
    if (this.overlayTexture_) {
      this.overlayTexture_.bind();
      this.overlayTexture_.activeTexture_ = gl.TEXTURE1;
      this.shader_.setTextureUniform("overlayTex", 1);
      this.shader_.setUniform1i("useTexture", 1);
      this.shader_.setUniform1f(
        "domainSize",
        this.brickDimension_ * this.brickDim_ * this.voxelSizeInMeter_
      );
    } else {
      this.shader_.setUniform1i("useTexture", 0);
    }
    this.shader_.setUniformMat4fv("VP", cameraViewProjection);
    const modelWorld = new Float32Array([
      0.2,
      0,
      0,
      0,
      0,
      0,
      (3 / 200) * 0.2,
      0,
      0,
      -0.2,
      0,
      0,
      0,
      this.brickDimension_ * this.brickDim_ * this.voxelSizeInMeter_,
      0,
      1,
    ]);
    this.shader_.setUniformMat4fv("modelWorld", modelWorld);
    this.shader_.setUniform1f("dimLight", lightValue);
    this.shader_.setUniform3fv("cameraDir", cameraDir);
    //this.shader_.setUniform1f("heightOffset", this.heightOffset_);
    this.shader_.setUniform1f("heightOffset", this.heightOffset_);
    this.shader_.setUniform1f("heightScale", this.heightScale_);

    if (renderPickingMode) {
      for (let i = this.voxelBrickIndices_.length - 1; i >= 0; i--) {
        let brickID = this.voxelBrickIndices_[i].index_;
        var vb = this.voxelBricks_[brickID];
        if (vb.isCulled_) {
          continue;
        }
        if (vb.brickClippingState_ == BrickClipState.FULLY_CLIPPED) {
          continue;
        }
        if (vb.vertexCount_ == 0) {
          continue;
        }

        this.shader_.bindProgram();
        this.shader_.setUniformMat4fv("VP", cameraViewProjection);
        this.shader_.setUniformMat4fv("V", cameraView);
        this.shader_.setUniform1f("anim", vb.animation_);
        this.shader_.setUniform1f("pickingMode", 1.0);
        this.shader_.setUniform1f("brickAlpha", 1.0);
        this.shader_.setUniform1f("dimLight", lightValue);
        this.shader_.setUniform3fv("cameraDir", cameraDir);
        this.shader_.setTextureUniform("VoxelTransferTexture", 0);

        vb.vertexBuffer_.bind();
        gl.drawArrays(wgRenderingPrimitives.TRIANGLES, 0, vb.vertexCount_);
        vb.vertexBuffer_.unbind();
        this.shader_.unbindProgram();
      }

      this.voxelTransferTexture_.unbind();
      gl.disable(gl.CULL_FACE);
      return;
    }
    // front to back opaque
    this.shader_.setUniform1f("brickAlpha", 1.0);

    for (let i = this.voxelBrickIndices_.length - 1; i >= 0; i--) {
      let brickID = this.voxelBrickIndices_[i].index_;
      let d = Math.max(0.0, this.voxelBrickIndices_[i].depth_);
      let alpha = d < 8 ? 0.0 : Math.min(1.0, (1.0 / 48.0) * d);
      if (alpha < 0.99) {
        continue;
      }

      let vb = this.voxelBricks_[brickID];
      vb.updateAnimation();

      if (vb.isCulled_) {
        continue;
      }
      if (vb.brickClippingState_ == BrickClipState.FULLY_CLIPPED) {
        continue;
      }
      if (vb.vertexCount_ == 0) {
        continue;
      }
      if (alpha < 0.0001) {
        continue;
      }

      this.shader_.setUniform1f("anim", vb.animation_);

      this.shader_.bindProgram();
      this.shader_.setUniformMat4fv("VP", cameraViewProjection);
      this.shader_.setUniformMat4fv("V", cameraView);
      this.shader_.setUniform1f("anim", vb.animation_);
      this.shader_.setUniform1f("pickingMode", -1.0);
      this.shader_.setUniform1f("brickAlpha", 1.0);
      this.shader_.setUniform1f("dimLight", lightValue);
      this.shader_.setUniform3fv("cameraDir", cameraDir);

      this.shader_.setTextureUniform("VoxelTransferTexture", 0);

      vb.vertexBuffer_.bind();
      gl.drawArrays(wgRenderingPrimitives.TRIANGLES, 0, vb.vertexCount_);
      vb.vertexBuffer_.unbind();
    }

    // transparent voxel bricks back to front rendering
    gl.enable(gl.CULL_FACE);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    // for (let i = 0; i < this.voxelBricks_.length; i++) {
    for (let i = 0; i < this.voxelBrickIndices_.length; i++) {
      //var vb = this.voxelBricks_[i];
      let brickID = this.voxelBrickIndices_[i].index_;
      let d = Math.max(0.0, this.voxelBrickIndices_[i].depth_);
      let alpha = d < 8 ? 0.0 : Math.min(1.0, (1.0 / 48.0) * d);
      if (alpha > 0.99) {
        continue;
      }

      let vb = this.voxelBricks_[brickID];
      vb.updateAnimation();

      if (vb.isCulled_) {
        continue;
      }
      if (vb.brickClippingState_ == BrickClipState.FULLY_CLIPPED) {
        continue;
      }
      if (vb.vertexCount_ == 0) {
        continue;
      }
      if (alpha < 0.0001) {
        continue;
      }

      this.shader_.bindProgram();
      this.shader_.setUniformMat4fv("VP", cameraViewProjection);
      this.shader_.setUniformMat4fv("V", cameraView);

      this.shader_.setUniform1f("anim", vb.animation_);
      this.shader_.setUniform1f("pickingMode", -1.0);
      this.shader_.setUniform1f("brickAlpha", alpha);
      this.shader_.setTextureUniform("VoxelTransferTexture", 0);

      vb.vertexBuffer_.bind();
      gl.drawArrays(wgRenderingPrimitives.TRIANGLES, 0, vb.vertexCount_);
      vb.vertexBuffer_.unbind();
    }

    this.voxelTransferTexture_.unbind();

    this.shader_.unbindProgram();
    gl.disable(gl.BLEND);
    gl.disable(gl.CULL_FACE);
  }

  createTestHeightMaps(mapDimension = gHeightMapDimension) {
    let gHm = new HeightMap(mapDimension);
    let ghMap = gHm.mapData();

    let sHm = new HeightMap(mapDimension);
    let shMap = sHm.mapData();

    let wHm = new HeightMap(mapDimension);
    let whMap = wHm.mapData();

    let cHm = new HeightMap(mapDimension);
    let chMap = cHm.mapData();

    let rHm = new HeightMap(mapDimension);
    let rhMap = rHm.mapData();

    const grassHeight = 120.0;
    const sandHeight = 90.0;
    const clayHeight = 40.0;
    const waterHeight = 25.0;
    const rockHeight = 10.0;

    if (this.grassLevel_ == null) {
      this.grassLevel_ = new Float32Array(mapDimension * mapDimension);
      this.sandLevel_ = new Float32Array(mapDimension * mapDimension);
      this.clayLevel_ = new Float32Array(mapDimension * mapDimension);
      this.waterLevel_ = new Float32Array(mapDimension * mapDimension);
      this.rockLevel_ = new Float32Array(mapDimension * mapDimension);
    }

    for (let y = 0; y < mapDimension; y++) {
      for (let x = 0; x < mapDimension; x++) {
        const index = x + y * mapDimension;

        let gh = ghMap[x][y];
        let sh = shMap[x][y];
        let wh = whMap[x][y];
        let ch = chMap[x][y];
        let rh = rhMap[x][y];

        let grassH = gh * grassHeight;
        let sandH = sh * sandHeight;
        let clayH = ch * clayHeight;
        let waterH = wh * waterHeight;
        let rockH = rh * rockHeight;

        this.grassLevel_[index] = grassH;
        this.sandLevel_[index] = sandH;
        this.clayLevel_[index] = clayH;
        this.waterLevel_[index] = waterH;
        this.rockLevel_[index] = rockH;
      }
    }
  }

  updateGlobalAo(vertexCount, vertexBufferData) {
    const dist = 1;
    for (let i = 0; i < vertexCount; i++) {
      let x = this.vertexBufferData_[i * 8];
      let y = this.vertexBufferData_[i * 8 + 1];
      let z = this.vertexBufferData_[i * 8 + 2];

      let grid = [];
      /* grid.push(this.getVoxelEmpty(x - dist, y, z- dist));
       grid.push(this.getVoxelEmpty(x , y, z-dist));
       grid.push(this.getVoxelEmpty(x - dist, y, z));
       grid.push(this.getVoxelEmpty(x, y, z));*/

      // grid.push(this.getVoxelEmpty(x, y, z));
      grid.push(this.getVoxelEmpty(x - dist, y, z - dist));
      grid.push(this.getVoxelEmpty(x - dist, y, z));
      grid.push(this.getVoxelEmpty(x, y, z - dist));
      grid.push(this.getVoxelEmpty(x, y, z));

      let numOcc = 0;

      for (let j = 0; j < 4; j++) {
        numOcc += grid[j] ? 1 : 0;
      }

      let vao = vertexBufferData[i * 8 + 7]; // the ao mask from the web asm
      // triangulation: -1 == clip edge.
      let ao = 1.0 - Math.min(1.0 / numOcc, 0.65);
      vertexBufferData[i * 8 + 7] = vao < 0.0 ? 1.0 : ao;
    }
  }

  updateBrick(index) {
    // fill the voxel brick

    let vb = this.voxelBricks_[index];
    vb.isValid_ = true;
    let xOffset = vb.x_ * this.brickDimension_;
    let yOffset = vb.y_ * this.brickDimension_;

    for (let z = 0; z < this.brickDimension_; z++) {
      for (let x = 0; x < this.brickDimension_; x++) {
        for (let y = 0; y < this.brickHeight_; y++) {
          const index =
            x +
            y * this.brickDimension_ +
            z * this.brickDimension_ * this.brickHeight_;
          this.voxelCopyBrick_[index] = 0;

          if (x + xOffset < this.clipPlanes_.clipXMin_) {
            continue;
          }
          if (x + xOffset > this.clipPlanes_.clipXMax_) {
            continue;
          }

          if (z + yOffset < this.clipPlanes_.clipZMin_) {
            continue;
          }
          if (z + yOffset > this.clipPlanes_.clipZMax_) {
            continue;
          }

          const hmIndex =
            x +
            xOffset +
            (this.voxelDimension_[2] - 1 - (z + yOffset)) *
              this.voxelDimension_[0] +
            y * this.voxelDimension_[0] * this.voxelDimension_[2];
          this.voxelCopyBrick_[index] = this.voxelData_[hmIndex];
        }
      }
    }

    if (vb.numberOfClipEdges_ == 1) {
      // vb.clipState == BrickClipState.CLIPPED)
      this.voxelBrickTriangulator_.setClipPolygon(
        this.brickClipPolygonEdges_.buffer
      );
      this.voxelBrickTriangulator_.setClipPolygonNormals(
        this.brickClipPolygonNormal_.buffer
      );
      this.voxelBrickTriangulator_.setNumberOfPolygonEdges(
        this.numberOfClipPolyEdges_
      );
    } else {
      this.voxelBrickTriangulator_.setNumberOfPolygonEdges(0);
    }

    this.voxelBrickTriangulator_.setVoxels(this.voxelCopyBrick_.buffer);
    this.voxelBrickTriangulator_.setOffsetAndScale(xOffset, 0.0, yOffset, 1.0);
    this.vertexBufferData_ = this.voxelBrickTriangulator_.triangulate();
    const vertexCount = this.vertexBufferData_.length / 8;

    this.updateGlobalAo(vertexCount, this.vertexBufferData_); // TEST

    // Add color from w component
    if (vertexCount >= gVertexBufferSize) {
      console.log("not enough room in vertex buffer:" + vertexCount);
      return;
    }

    vb.vertexBuffer_.updateVertexBufferData(0, this.vertexBufferData_);
    vb.vertexCount_ = vertexCount;
    this.totalVertexCount_ += vertexCount;
    //   console.log("total Triangle count" + this.totalVertexCount_ / 3);
  }
}

export { VoxelModel };
